import {
  CUSTOM_SPEC_TYPES,
  type CannabinoidEntry,
  type CannabinoidName,
  type CannabinoidUnit,
  type CustomSpecification,
  type CustomSpecType,
  type HiddenSpecField,
  type ProductDraft,
  type SpecSet,
  type VariationGroupItem,
  type VariationSpecsEntry,
} from '../../../types/product'

// Per-variant specifications (ACD-15223). The default variant is
// variation_group[0]; its specs live in the product-level draft fields
// (cannabinoids, volume, terpenes, …). Every other variant has an entry in
// `draft.variationSpecs` that either inherits the default set or carries its
// own values. Pure helpers only — shared by the editor hook, the payload
// builder and the Specifications / Variations UI.

export const CANNABINOID_NAMES: CannabinoidName[] = ['THC', 'CBD', 'CBN', 'CBG']
export const DEFAULT_CANNABINOID_UNIT: CannabinoidUnit = 'mg'

// ── Parsing (raw variation docs → editor values) ──

export function extractTerpeneNames(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const names: string[] = []
  for (const entry of raw) {
    if (typeof entry === 'string') {
      const trimmed = entry.trim()
      if (trimmed) names.push(trimmed)
    } else if (entry && typeof entry === 'object') {
      const name = (entry as { name?: unknown }).name
      if (typeof name === 'string' && name.trim()) names.push(name.trim())
    }
  }
  return names
}

export function extractStringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const entry of raw) {
    if (typeof entry === 'string') {
      const trimmed = entry.trim()
      if (trimmed) out.push(trimmed)
    }
  }
  return out
}

function toNumberOrNull(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function isCustomSpecType(raw: unknown): raw is CustomSpecType {
  return typeof raw === 'string' && (CUSTOM_SPEC_TYPES as string[]).includes(raw)
}

// A new, empty custom specification row of the given type.
export function emptyCustomSpec(type: CustomSpecType = 'text'): CustomSpecification {
  return { key: '', type, value: '', number: null, min: null, max: null, unit: '', has_unit: false, items: [] }
}

// Normalizes raw `bs_custom_specifications` into fully-populated typed rows.
// Legacy `{ key, value }` docs (or an unknown type) read as 'text'. Rows are
// kept even with a blank key — blank rows are dropped only at save
// (see sanitizeCustomSpecsForSave).
export function extractCustomSpecs(raw: unknown): CustomSpecification[] {
  if (!Array.isArray(raw)) return []
  const out: CustomSpecification[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const e = entry as Record<string, unknown>
    out.push({
      key: typeof e.key === 'string' ? e.key.trim() : '',
      type: isCustomSpecType(e.type) ? e.type : 'text',
      value: typeof e.value === 'string' ? e.value : e.value == null ? '' : String(e.value),
      number: toNumberOrNull(e.number),
      min: toNumberOrNull(e.min),
      max: toNumberOrNull(e.max),
      unit: typeof e.unit === 'string' ? e.unit : '',
      has_unit: e.has_unit === true,
      items: Array.isArray(e.items) ? e.items.filter((i): i is string => typeof i === 'string') : [],
    })
  }
  return out
}

function nullToUndef(v: number | null | undefined): number | undefined {
  return v == null ? undefined : v
}

export function cannabinoidsFromItem(item: VariationGroupItem): CannabinoidEntry[] {
  return [
    { name: 'THC', min: nullToUndef(item.thc_min), max: nullToUndef(item.thc_max), unit: (item.thc_unit || DEFAULT_CANNABINOID_UNIT) as CannabinoidUnit },
    { name: 'CBD', min: nullToUndef(item.cbd_min), max: nullToUndef(item.cbd_max), unit: (item.cbd_unit || DEFAULT_CANNABINOID_UNIT) as CannabinoidUnit },
    { name: 'CBN', min: nullToUndef(item.cbn_min), max: nullToUndef(item.cbn_max), unit: (item.cbn_unit || DEFAULT_CANNABINOID_UNIT) as CannabinoidUnit },
    { name: 'CBG', min: nullToUndef(item.cbg_min), max: nullToUndef(item.cbg_max), unit: (item.cbg_unit || DEFAULT_CANNABINOID_UNIT) as CannabinoidUnit },
  ]
}

// The spec values stored on one variation doc. Volume / effects / flavours /
// custom specs fall back to the first location entry when the item-level
// field is missing (same as the editor's historical seeding).
export function specSetFromItem(item: VariationGroupItem): SpecSet {
  const loc = item.locations?.[0]
  const volume = item.volume ?? loc?.volume ?? null
  return {
    cannabinoids: cannabinoidsFromItem(item),
    volume: typeof volume === 'number' ? volume : null,
    terpenes: extractTerpeneNames(item.terpenes ?? loc?.terpenes),
    effects: extractStringList(item.effects ?? loc?.effects),
    flavours: extractStringList(item.flavours ?? loc?.flavours),
    bs_custom_specifications: extractCustomSpecs(item.bs_custom_specifications ?? loc?.bs_custom_specifications),
  }
}

// Stored `bs_inherit_specs` for a variation. Index 0 (default) never inherits;
// a flag that was never written (undefined / null) reads as inheriting.
export function readInheritFlag(item: VariationGroupItem, index: number): boolean {
  if (index === 0) return false
  return item.bs_inherit_specs ?? item.locations?.[0]?.bs_inherit_specs ?? true
}

// Seeds `draft.variationSpecs` for every non-default variation.
export function seedVariationSpecs(items: VariationGroupItem[]): Record<string, VariationSpecsEntry> {
  const out: Record<string, VariationSpecsEntry> = {}
  items.forEach((item, index) => {
    if (index === 0) return
    out[item._id] = { inherit: readInheritFlag(item, index), specs: specSetFromItem(item) }
  })
  return out
}

// Display name of a variation: its "variations" attribute option (e.g.
// "3.5g"), else the first attribute option, else the item name / id.
export function variantLabel(item: Pick<VariationGroupItem, '_id' | 'name' | 'attributes'>): string {
  if (item.attributes?.length) {
    const attr = item.attributes.find((a) => a.name === 'variations') ?? item.attributes[0]
    if (attr?.option) return attr.option
  }
  return item.name ?? item._id
}

// ── Draft accessors ──

export function emptySpecSet(): SpecSet {
  return {
    cannabinoids: CANNABINOID_NAMES.map((name) => ({ name, unit: DEFAULT_CANNABINOID_UNIT })),
    volume: null,
    terpenes: [],
    effects: [],
    flavours: [],
    bs_custom_specifications: [],
  }
}

// Deep copy, so a dialog can edit a set without mutating the draft.
export function cloneSpecSet(specs: SpecSet): SpecSet {
  return {
    cannabinoids: specs.cannabinoids.map((c) => ({ ...c })),
    volume: specs.volume,
    terpenes: [...specs.terpenes],
    effects: [...specs.effects],
    flavours: [...specs.flavours],
    bs_custom_specifications: specs.bs_custom_specifications.map((s) => ({
      ...s,
      ...(s.items ? { items: [...s.items] } : {}),
    })),
  }
}

// True when a set carries no values: no cannabinoid min/max, no volume, no
// terpenes / effects / flavours, no custom spec with a non-blank key.
export function isEmptySpecSet(specs: SpecSet): boolean {
  return (
    specs.cannabinoids.every((c) => c.min == null && c.max == null) &&
    specs.volume == null &&
    specs.terpenes.length === 0 &&
    specs.effects.length === 0 &&
    specs.flavours.length === 0 &&
    specs.bs_custom_specifications.every((s) => !s.key.trim())
  )
}

// The default variant's spec set (the product-level draft fields).
export function defaultSpecSetFromDraft(draft: Pick<ProductDraft, keyof SpecSet>): SpecSet {
  return {
    cannabinoids: draft.cannabinoids,
    volume: draft.volume,
    terpenes: draft.terpenes,
    effects: draft.effects,
    flavours: draft.flavours,
    bs_custom_specifications: draft.bs_custom_specifications,
  }
}

// True when a non-default variation has its own specifications.
export function isCustom(draft: Pick<ProductDraft, 'variationSpecs'>, itemId: string): boolean {
  return draft.variationSpecs?.[itemId]?.inherit === false
}

// The specs a variation actually uses: the default set for index 0, for an
// inheriting variation, or for one not seeded yet; otherwise its own set.
export function effectiveSpecs(
  draft: Pick<ProductDraft, keyof SpecSet | 'variationSpecs'>,
  item: Pick<VariationGroupItem, '_id'>,
  index: number,
): SpecSet {
  const entry = draft.variationSpecs?.[item._id]
  if (index === 0 || !entry || entry.inherit !== false) return defaultSpecSetFromDraft(draft)
  return entry.specs
}

export interface CustomVariantSpecs {
  itemId: string
  // Position in variation_group (always ≥ 1).
  index: number
  specs: SpecSet
}

// Non-default variations with custom specs, in variation_group order.
export function customVariants(
  draft: Pick<ProductDraft, 'variationSpecs'>,
  items: VariationGroupItem[],
): CustomVariantSpecs[] {
  const out: CustomVariantSpecs[] = []
  items.forEach((item, index) => {
    const entry = draft.variationSpecs?.[item._id]
    if (index > 0 && entry && entry.inherit === false) out.push({ itemId: item._id, index, specs: entry.specs })
  })
  return out
}

// ── Formatting (must match the backend's format_custom_spec_value) ──

function expandExponent(text: string): string {
  const [mantissa, expText] = text.toLowerCase().split('e')
  const negative = mantissa.startsWith('-')
  const [intPart, frac = ''] = (negative ? mantissa.slice(1) : mantissa).split('.')
  const digits = intPart + frac
  const point = intPart.length + Number(expText)
  let out: string
  if (point <= 0) out = `0.${'0'.repeat(-point)}${digits}`
  else if (point >= digits.length) out = digits + '0'.repeat(point - digits.length)
  else out = `${digits.slice(0, point)}.${digits.slice(point)}`
  return (negative ? '-' : '') + out
}

// 12 → "12", 0.5 → "0.5", 1e21 → "1000000000000000000000" (never exponent notation).
export function formatSpecNumber(n: number): string {
  if (!Number.isFinite(n)) return ''
  let text = String(n)
  if (/e/i.test(text)) text = expandExponent(text)
  if (text.includes('.')) text = text.replace(/0+$/, '').replace(/\.$/, '')
  return text === '-0' ? '0' : text
}

// Display value of a custom specification ('' when empty). Same rules as the
// backend's stored `value`: number → "12 mg"; range → "1–2 %" (U+2013), one
// end or equal ends → that number; list → "a, b"; text/longtext → `value`.
// The unit is appended only when `has_unit` and `unit` are both set.
export function formatCustomSpecValue(spec: CustomSpecification): string {
  const type = spec.type ?? 'text'
  const suffix = spec.has_unit && spec.unit ? ` ${spec.unit}` : ''
  if (type === 'number') {
    return spec.number == null ? '' : formatSpecNumber(spec.number) + suffix
  }
  if (type === 'range') {
    const { min, max } = spec
    if (min == null && max == null) return ''
    if (min == null || max == null) return formatSpecNumber((min ?? max) as number) + suffix
    if (min === max) return formatSpecNumber(min) + suffix
    return `${formatSpecNumber(min)}–${formatSpecNumber(max)}${suffix}`
  }
  if (type === 'list') return (spec.items ?? []).join(', ')
  return spec.value ?? ''
}

// "20–22 %", "20 %" for a one-sided or equal range, '' when neither end is set.
export function formatCannabinoidRange(entry: CannabinoidEntry | undefined): string {
  if (!entry || (entry.min == null && entry.max == null)) return ''
  const { min, max } = entry
  let range: string
  if (min == null || max == null) range = formatSpecNumber((min ?? max) as number)
  else if (min === max) range = formatSpecNumber(min)
  else range = `${formatSpecNumber(min)}–${formatSpecNumber(max)}`
  return `${range} ${entry.unit}`
}

// Rows to persist: blank-key rows dropped; number/range/list `value`
// recomputed so the payload matches what the backend will store.
export function sanitizeCustomSpecsForSave(specs: CustomSpecification[]): CustomSpecification[] {
  return specs
    .filter((s) => s.key.trim() !== '')
    .map((s) => {
      const type = s.type ?? 'text'
      const derived = type === 'number' || type === 'range' || type === 'list'
      return { ...s, key: s.key.trim(), type, value: derived ? formatCustomSpecValue(s) : (s.value ?? '') }
    })
}

// ── Diff against the default variant (Specifications section ◆ lines) ──

const normLabel = (v: string | null | undefined) => String(v ?? '').trim().toLowerCase()

// `text` is the variant's display value; '' means "not set".
export interface VariantValueDiff {
  itemId: string
  text: string
}

export interface VariantListDiff {
  itemId: string
  added: string[]
  removed: string[]
}

export interface VariantExtraSpec {
  itemId: string
  key: string
  text: string
}

export interface SpecDiff {
  // Only cannabinoids with at least one differing variant have a non-empty list.
  cannabinoids: Record<CannabinoidName, VariantValueDiff[]>
  volume: VariantValueDiff[]
  terpenes: VariantListDiff[]
  effects: VariantListDiff[]
  flavours: VariantListDiff[]
  // Index-aligned with the default set's bs_custom_specifications; matched by
  // case-insensitive trimmed label. Blank-label default rows get [].
  customSpecs: VariantValueDiff[][]
  // Labelled custom specs that exist only on variants ("Only on variants:").
  onlyOnVariants: VariantExtraSpec[]
}

const volumeText = (v: number | null | undefined) => (v == null ? '' : formatSpecNumber(v))

function listDiff(base: string[], variant: string[]): { added: string[]; removed: string[] } {
  return {
    added: variant.filter((x) => !base.includes(x)),
    removed: base.filter((x) => !variant.includes(x)),
  }
}

// Where each custom variant differs from the default set. Hidden fields
// produce empty results.
export function diffAgainstDefault(
  defaultSpecs: SpecSet,
  variants: Pick<CustomVariantSpecs, 'itemId' | 'specs'>[],
  hidden: readonly HiddenSpecField[] = [],
): SpecDiff {
  const isHidden = (f: HiddenSpecField) => hidden.includes(f)
  const cannabinoids = {} as Record<CannabinoidName, VariantValueDiff[]>
  for (const name of CANNABINOID_NAMES) {
    const base = formatCannabinoidRange(defaultSpecs.cannabinoids.find((c) => c.name === name))
    cannabinoids[name] = isHidden(name)
      ? []
      : variants
          .map(({ itemId, specs }) => ({
            itemId,
            text: formatCannabinoidRange(specs.cannabinoids.find((c) => c.name === name)),
          }))
          .filter((d) => d.text !== base)
  }

  const baseVolume = volumeText(defaultSpecs.volume)
  const volume = variants
    .map(({ itemId, specs }) => ({ itemId, text: volumeText(specs.volume) }))
    .filter((d) => normLabel(d.text) !== normLabel(baseVolume))

  const lists = (field: 'terpenes' | 'effects' | 'flavours'): VariantListDiff[] =>
    isHidden(field)
      ? []
      : variants
          .map(({ itemId, specs }) => ({ itemId, ...listDiff(defaultSpecs[field], specs[field]) }))
          .filter((d) => d.added.length > 0 || d.removed.length > 0)

  const customSpecs = defaultSpecs.bs_custom_specifications.map((base) => {
    if (!normLabel(base.key)) return []
    const baseText = formatCustomSpecValue(base)
    const out: VariantValueDiff[] = []
    for (const { itemId, specs } of variants) {
      const match = specs.bs_custom_specifications.find((s) => normLabel(s.key) === normLabel(base.key))
      if (!match) out.push({ itemId, text: '' })
      else if (formatCustomSpecValue(match) !== baseText) out.push({ itemId, text: formatCustomSpecValue(match) })
    }
    return out
  })

  const baseLabels = new Set(defaultSpecs.bs_custom_specifications.map((s) => normLabel(s.key)))
  const onlyOnVariants: VariantExtraSpec[] = []
  for (const { itemId, specs } of variants) {
    for (const s of specs.bs_custom_specifications) {
      const label = normLabel(s.key)
      if (!label || baseLabels.has(label)) continue
      onlyOnVariants.push({ itemId, key: s.key.trim(), text: formatCustomSpecValue(s) })
    }
  }

  return {
    cannabinoids,
    volume,
    terpenes: lists('terpenes'),
    effects: lists('effects'),
    flavours: lists('flavours'),
    customSpecs,
    onlyOnVariants,
  }
}
